// ============================================================================
// THE DATA CONTRACT — index.ts
//
// Picks an implementation and wraps every method in a TanStack Query hook.
// Components import the hooks. They never learn whether a database exists.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  addDays,
  startOfWeek,
  type Announcement,
  type CalendarBlock,
  type CalendarRange,
  type ConnectionsScenario,
  type ISODate,
  type JournalEntry,
  type Task,
} from '@app/shared'

import { remoteConfig } from '../auth/supabase'
// The durable journal draft. Its own import line — the save mutation is the one place that can
// prove a draft is safe to forget, and parallel lanes edit this file's import block.
import { confirmJournalDraft, journalDraftStorage } from '../journal-draft'
import { useSession } from '../auth/useSession'
import { listStagedShares } from '../shared-audio'
import {
  SIGNED_OUT_NOTE,
  STORAGE_REFUSED,
  createPassGate,
  importNativeRecording,
  importStagedShares,
  openQueueStore,
  removeEntry,
  retryNow,
  runQueueOnce,
  sendAsNewUpload,
  type ImportOutcome,
  type QueueEntry,
  type QueueStore,
} from '../upload-queue'
import { demo } from './demo'
import { remote } from './remote'
import type {
  AnnouncementFilter,
  AssignmentFilter,
  ChatThreadDetail,
  Clock,
  DataAPI,
  DateRange,
  JournalEntryInput,
  SaveFilter,
  TaskFilter,
  TaskInput,
  TaskPatch,
  VoiceMemoUpload,
} from './types'
// The ⌘K capture palette's write. Its own import line rather than another name above: three
// screen ports are in flight at once and a one-identifier edit inside a shared import list is a
// merge conflict for no benefit.
import type { CaptureInput } from './types'
// Learning v2 — what a session write may carry beyond the tick. Its own line, same convention.
import type { LearningSessionInput } from './types'
// Calendar (11a/11b). Same reason for the separate line.
import type { CalendarBlockInput, CalendarBlockPatch } from './types'
// Notifications · Web Push (NOTIF-1b). Same convention — its own line.
import type { NotificationFilter, NotificationSettingsPatch } from './types'
// The page half of Web Push. It takes the API as an argument rather than importing `data`, which
// is what keeps this import from being a cycle (lib/push.ts says so in its header).
import {
  browserEndpoint,
  flushPendingResubscription,
  subscribe,
  subscriptionState,
  unsubscribe,
} from '../push'
// The one chat write (V4). Its own line, for the same reason.
import type { ChatSendInput } from './types'
// Chat v3 — pictures and proposals. Same convention: its own line.
import type { ChatAttachment, ChatProposalDecision } from './types'
// The phone lecture upload (`/record`). Same reason.
import type { LectureUpload } from './types'
// Mail messages (Lane 1b). Same convention: its own line.
import type { MailMessageFilter } from './types'
// Mail triage (Day page prototype). Same convention.
import type { MailCorrectionReason, MailItemState, MailRuleInput, MailVerdict } from './types'
// The designed Day page (Lane 4a). Same convention.
import type { DayScenario, MailTriageQuery } from './types'
// A write that can ring asks the phone app to re-read its alarms at once. Its own line.
import { armPhoneAlarmsNow } from '../phone-alarms'

/**
 * Demo mode is the DEFAULT, not a fallback. With no configuration at all the app
 * is fully populated — that is what makes it possible to build and review any
 * screen without an account (blueprint/01 → the data contract).
 *
 * The choice is made by CONFIGURATION, not by session: all three of VITE_SUPABASE_URL,
 * VITE_SUPABASE_ANON_KEY and VITE_API_BASE_URL present means this build talks to the Worker, full
 * stop. The session decides whether the app renders at all (components/AuthGate.tsx) — no hook
 * below ever runs without one — so this value can stay a constant instead of changing under a
 * component mid-render.
 */
export const isDemoMode = remoteConfig() === null

export const data: DataAPI = isDemoMode ? demo : remote

export * from './types'

// ── meta ────────────────────────────────────────────────────────────────────

// Factored out (rather than left as literals inside each hook) for exactly one reason:
// `prefetchCoreScreens` below has to ask for the SAME cache entry a screen's own hook will read a
// moment later, and two independently-typed arrays that happen to look alike today are a silent
// cache miss waiting for one of them to be edited without the other.
const clockKey = ['clock'] as const
const termKey = ['term'] as const
const journalEntryKey = (date: ISODate | undefined) => ['journalEntry', date] as const
const voiceMemosKey = (date: ISODate | undefined) => ['voiceMemos', date] as const
const remindersKey = ['reminders'] as const
const assignmentsKey = (filter?: AssignmentFilter) => ['assignments', filter] as const
const learningItemsKey = (date: ISODate | undefined) => ['learningItems', date] as const
const tasksKey = (filter?: TaskFilter) => ['tasks', filter] as const
const calendarKey = (range: DateRange | undefined) => ['calendar', range?.from, range?.to] as const
const learningTrackSummariesKey = ['learningTrackSummaries'] as const
const learningResourcesKey = ['learningResources'] as const
const coursesKey = ['courses'] as const

/**
 * The app's "now". Never call Date.now() in a component — demo mode would drift.
 *
 * `live` re-reads it every 30 s while the caller is mounted, for a line that compares a time the
 * owner is picking against the minute it is now (components/tasks/PhoneMayMissNote.tsx): a clock
 * read when the board opened an hour ago would be an hour wrong about "15 minutes from now".
 * Demo mode's clock is pinned, so re-reading it changes nothing there.
 */
export function useClock(options?: { live?: boolean }) {
  return useQuery({
    queryKey: clockKey,
    queryFn: () => data.getClock(),
    staleTime: 60_000,
    ...(options?.live ? { refetchInterval: 30_000 } : {}),
  })
}

export function useSyncStatus() {
  return useQuery({ queryKey: ['syncStatus'], queryFn: () => data.getSyncStatus() })
}

// ── journal ─────────────────────────────────────────────────────────────────

export function useJournalEntry(date: ISODate | undefined) {
  return useQuery({
    queryKey: journalEntryKey(date),
    queryFn: () => data.getJournalEntry(date!),
    enabled: Boolean(date),
  })
}

export function useJournalEntries(range: DateRange | undefined) {
  return useQuery({
    queryKey: ['journalEntries', range?.from, range?.to],
    queryFn: () => data.getJournalEntries(range!),
    enabled: Boolean(range),
  })
}

/**
 * The Journal index's totals, streak, heatmap window, tag counts and month coverage — one read,
 * because every number on that screen has to agree with every other one.
 */
export function useJournalStats() {
  return useQuery({ queryKey: ['journalStats'], queryFn: () => data.getJournalStats() })
}

/** The read-only "that day" strip on the day page. */
export function useJournalDayContext(date: ISODate | undefined) {
  return useQuery({
    queryKey: ['journalDayContext', date],
    queryFn: () => data.getJournalDayContext(date!),
    enabled: Boolean(date),
  })
}

/**
 * How often a day with an unfinished memo re-asks. The desktop worker polls the queue on its own
 * clock and a memo takes seconds to transcribe once claimed, so anything faster is noise and
 * anything slower makes a finished transcript feel stuck.
 */
const MEMO_POLL_MS = 20_000

/**
 * The memos dropped into one day. Empty is a real answer.
 *
 * IT POLLS, BUT ONLY WHILE THERE IS SOMETHING TO WAIT FOR. A memo goes queued → transcribed
 * because of work happening on another machine; nothing pushes that back here, and asking the
 * owner to reload to find out whether his own recording came back is the app making him the
 * transport. So: an interval while any memo on this day is unfinished, and `false` — no timer at
 * all — the moment none is. `refetchIntervalInBackground` is left at its default of false, so a
 * backgrounded tab stops asking entirely rather than polling a screen nobody is looking at.
 */
export function useVoiceMemos(date: ISODate | undefined) {
  return useQuery({
    queryKey: voiceMemosKey(date),
    queryFn: () => data.getVoiceMemos(date!),
    enabled: Boolean(date),
    refetchInterval: (query) => {
      const memos = query.state.data
      if (!Array.isArray(memos)) return false
      return memos.some((memo) => memo.status === 'queued' || memo.status === 'recording')
        ? MEMO_POLL_MS
        : false
    },
  })
}

/**
 * Send a finished recording to the transcription pipeline.
 *
 * The day's memo list is INVALIDATED rather than optimistically appended: the server decides what
 * a memo is (it is a job row joined to a transcript), and a locally-invented row would be a second
 * account of the same recording that can disagree with the first. The refetch that follows brings
 * back the real `queued` row, which then starts the poll above on its own.
 */
export function useUploadVoiceMemo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: VoiceMemoUpload) => data.uploadVoiceMemo(input),
    onSuccess: (_memo, input) => {
      void client.invalidateQueries({ queryKey: ['voiceMemos', input.date] })
    },
  })
}

/**
 * Autosave, and the three ways it could quietly lose writing:
 *
 *   1. LATENCY. Demo mode answers instantly; a real round trip does not. Without an optimistic
 *      update, tapping a mood does nothing visible for 200ms and the natural reaction is to tap
 *      again. So the cache is updated from the input first, and rolled back only if the save fails.
 *   2. OUT-OF-ORDER RESPONSES. Two saves in flight, the older one answers last, and writing the
 *      server's answer into the cache would move the entry BACKWARDS. `seq` makes a stale response
 *      update nothing: the newest optimistic state is already the truth. Since JRN-1b the
 *      requests no longer overlap at all (the mutation `scope` below), so this is now the belt
 *      beside the braces rather than the only defence — and it is KEPT, because it is the thing
 *      that still holds if a caller ever mutates outside the scope.
 *   2b. OUT-OF-ORDER WRITES, which `seq` never could reach: the Worker PUT reads, merges and
 *      writes with no version check, so an older request landing last leaves the DATABASE older
 *      than the screen. Only serializing the requests fixes that — see `scope` below.
 *   3. A FAILED SAVE THAT LOOKS LIKE A SAVED ONE. On error the cache goes back to what the server
 *      last confirmed, and the card shows the failure — the textarea keeps what was typed (it is
 *      uncontrolled, see JournalEntryCard), so nothing is thrown away, but nothing claims to be
 *      saved either.
 */
let saveSeq = 0

function optimisticEntry(
  previous: JournalEntry | null | undefined,
  input: JournalEntryInput,
): JournalEntry {
  const text = input.text !== undefined ? input.text : (previous?.text ?? '')
  const trimmed = text.trim()
  return {
    date: input.date,
    prompt: previous?.prompt ?? '',
    text,
    wordCount: trimmed ? trimmed.split(/\s+/).length : 0,
    mood: input.mood !== undefined ? input.mood : (previous?.mood ?? null),
    energy: input.energy !== undefined ? input.energy : (previous?.energy ?? null),
    tags: input.tags ?? previous?.tags ?? [],
    voiceMemoCount: previous?.voiceMemoCount ?? 0,
    updatedAt: new Date().toISOString(),
  }
}

export function useSaveJournalEntry(date: ISODate) {
  const qc = useQueryClient()
  return useMutation({
    // ONE DAY, ONE SAVE AT A TIME (JRN-1b).
    //
    // A mutation scope makes TanStack queue same-scope mutations instead of running them
    // concurrently — the optimistic update still happens immediately, only the REQUEST waits.
    // That is the fix for a race no client-side bookkeeping could reach: the Worker's PUT is a
    // read-merge-write with no version check, so two overlapping requests both read the row and
    // the one that lands LAST wins the row. `saveSeq` below only discards a stale RESPONSE — the
    // database had already been written by then. Serialize, and "last request sent" and "last row
    // written" become the same thing again.
    //
    // The id is per DAY, so today's autosave and today's "+ tag" write (its own hook instance,
    // same day) queue behind each other, while two different days never block one another. The
    // one seam: a composer flush that lands after midnight carries yesterday's date while this
    // scope already says today — harmless, because that write is the only one for that day.
    scope: { id: `journal-${date}` },
    mutationFn: (input: JournalEntryInput) => data.saveJournalEntry(input),

    onMutate: async (input: JournalEntryInput) => {
      const key = ['journalEntry', input.date]
      // Stop an in-flight read from landing on top of the optimistic write.
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<JournalEntry | null>(key)
      qc.setQueryData(key, optimisticEntry(previous, input))
      return { previous, key, seq: ++saveSeq }
    },

    onError: (_error, _input, context) => {
      if (context) qc.setQueryData(context.key, context.previous)
    },

    onSuccess: (entry, _input, context) => {
      // THE DURABLE DRAFT IS CLEARED HERE, NOT IN THE COMPONENT — and the difference is the
      // whole reason the draft exists. TanStack v5 does not run a `mutate(…, { onSuccess })`
      // callback once its observer has unmounted, and the commonest save of all is the flush
      // fired BY that unmount (leaving the tab mid-sentence). A component-level clear would
      // therefore leave a confirmed draft on the phone forever, and the composer would keep
      // saying "not saved" about words that are. This callback belongs to the mutation, so it
      // runs either way. It clears only when the server confirms EXACTLY the stored words —
      // never on a mere "a save succeeded" (see lib/journal-draft.ts).
      confirmJournalDraft(journalDraftStorage(), entry.date, entry.text)
      // A response from an older save has nothing to teach the cache.
      if (context && context.seq !== saveSeq) return
      qc.setQueryData(['journalEntry', entry.date], entry)
      void qc.invalidateQueries({ queryKey: ['journalEntries'] })
      // The archive's totals, streak and heatmap are all derived from the entries, so a write
      // that did not refresh them would leave the Journal index quietly one entry behind.
      void qc.invalidateQueries({ queryKey: ['journalStats'] })
    },
  })
}

// ── school ──────────────────────────────────────────────────────────────────

/** The term is what turns a date into "Week 4"; it changes twice a year, so it barely goes stale. */
export function useTerm() {
  return useQuery({ queryKey: termKey, queryFn: () => data.getTerm(), staleTime: 60 * 60_000 })
}

export function useCourses() {
  return useQuery({ queryKey: coursesKey, queryFn: () => data.getCourses() })
}

export function useClassOccurrences(date: ISODate | undefined) {
  return useQuery({
    queryKey: ['classOccurrences', date],
    queryFn: () => data.getClassOccurrences(date!),
    enabled: Boolean(date),
  })
}

/**
 * The next `days` days of class meetings, one query per day.
 *
 * WHY NOT A RANGE METHOD. `getClassOccurrences` is date-scoped and real in both modes; a range
 * variant would be a new DataAPI method, and therefore a new Worker route, a new demo
 * implementation and a new contract — for a caller that wants exactly what these already answer.
 * Seven cached day-queries reuse today's, which every School screen has already fetched.
 *
 * WHO WANTS IT. Only the native shell, which arms real Android alarms for class time and needs a
 * week of them at once (`apps/web/src/lib/native-recorder.ts` → `scheduleNativeClasses`). It is
 * `enabled`-gated for that reason: a browser tab must not fire six extra requests for a capability
 * it does not have.
 *
 * `pending` stays true until every day has answered, because arming a PARTIAL week would cancel
 * the alarms already set and replace them with fewer.
 */
export function useClassOccurrenceWeek(
  from: ISODate | undefined,
  options: { enabled?: boolean; days?: number } = {},
) {
  const enabled = (options.enabled ?? true) && Boolean(from)
  const days = options.days ?? 7
  const dates = from ? Array.from({ length: days }, (_, i) => addDays(from, i)) : []

  const results = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['classOccurrences', date],
      queryFn: () => data.getClassOccurrences(date),
      enabled,
    })),
  })

  return {
    dates,
    pending: !enabled || results.some((r) => r.isPending),
    error: results.find((r) => r.error)?.error ?? null,
    days: results.map((r, i) => ({ date: dates[i]!, occurrences: r.data ?? [] })),
  }
}

export function useAssignments(filter?: AssignmentFilter) {
  return useQuery({
    queryKey: assignmentsKey(filter),
    queryFn: () => data.getAssignments(filter),
  })
}

export function useLectures(courseId?: string) {
  return useQuery({ queryKey: ['lectures', courseId], queryFn: () => data.getLectures(courseId) })
}

export function useLectureTranscript(lectureId: string | undefined) {
  return useQuery({
    queryKey: ['lectureTranscript', lectureId],
    queryFn: () => data.getLectureTranscript(lectureId!),
    enabled: Boolean(lectureId),
  })
}

// ── phone lecture upload (`/record`) ────────────────────────────────────────

/** What the phone and the drop folder have sent, newest first. */
export function useCapturedLectures(limit?: number) {
  return useQuery({
    queryKey: ['capturedLectures', limit ?? null],
    queryFn: () => data.getCapturedLectures(limit),
  })
}

/** One recording by id. `null` data is "no such lecture", not a failure. */
export function useCapturedLecture(id: string | undefined) {
  return useQuery({
    queryKey: ['capturedLecture', id],
    queryFn: () => data.getCapturedLecture(id!),
    enabled: Boolean(id),
  })
}

/**
 * Send one lecture recording up.
 *
 * NOT wrapped in an optimistic update, and the list is invalidated rather than appended to: the
 * server decides what a lecture is (a row whose status moved because the bytes arrived), and a
 * locally-invented row would be a second account of the same recording that can disagree with the
 * first — the same rule the voice-memo upload follows.
 *
 * The progress callback and the abort signal ride on the input rather than on the hook: they
 * belong to ONE attempt, and a retry is a new attempt with a new signal.
 */
export function useUploadLecture() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: LectureUpload) => data.uploadLecture(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['capturedLectures'] })
    },
  })
}

// ── the durable upload queue (REC-1b) ───────────────────────────────────────
//
// NOT A TANSTACK QUERY, and the reason is where the data lives: this queue is IndexedDB on the
// phone, not a read off the Worker. A react-query cache keyed on the server would be a second,
// staler account of a recording whose whole point is that it survives the app. The three DataAPI
// methods it drives (`registerUpload` / `putUploadBlob` / `getUploadState`) are contract methods
// like any other; this hook is the fourth corner of that contract for all three.

/** What `/record` renders and what its rows can do. */
export interface UploadQueueView {
  entries: QueueEntry[]
  /** True once the phone's store is open. False in demo mode, and while it is opening. */
  ready: boolean
  /**
   * The one sentence the screen shows above the list, or null.
   *
   * Paused ("signed out — open LifeOS to continue"), refused ("not saved — storage refused"), or
   * nothing at all. It is never a status invented from a failed request.
   */
  note: string | null
  /** Move this one now — clears any Retry-After the Worker asked for. */
  retry: (id: string) => void
  /** Forget LifeOS's copy. The recording in the Samsung app is untouched. */
  remove: (id: string) => void
  /** Mint a new upload identity and start over. For a job the server has closed. */
  sendAsNew: (id: string) => void
  /**
   * Take a recording the native shell just finished (REC-1c) into this same queue.
   *
   * It answers the OUTCOME rather than throwing, because "the app cannot hand over the bytes yet"
   * is a sentence the screen has to show under a recording that really does exist on the phone,
   * not an exception to swallow. The queue never invents an entry it has no bytes for.
   */
  importNative: (input: NativeImport) => Promise<ImportOutcome>
}

/**
 * What demo mode says when something asks the queue to take a recording.
 *
 * It refuses for the same reason `demo.registerUpload` does: there is no bucket and no desktop
 * GPU behind a demo, and a queue row that said "sending" would be a fixture claiming to be a
 * pipeline. The recording is not lost — in demo mode nothing recorded it in the first place.
 */
export const NO_QUEUE_IN_DEMO =
  'Demo mode has no upload path, so nothing was queued. Recording needs a signed-in account, ' +
  'R2 and the desktop transcriber.'

/** What the Recordings screen hands over after `stop()`. The hook supplies the store and the day. */
export interface NativeImport {
  path: string
  courseCode: string | null
  blob: Blob | null
  durationSec?: number | null
  /**
   * The day the recording was MADE, when that is known and is not today — an alarm recording
   * picked up the morning after its class. Absent means today, as it always did.
   */
  date?: ISODate
  /** A title the server should use instead of the file name (the recorder self-test only). */
  title?: string | null
}

/**
 * How often a VISIBLE tab takes a pass. Hidden tabs do nothing — a phone in a pocket with the
 * PWA backgrounded is not uploading, and no copy anywhere says it is.
 */
const QUEUE_INTERVAL_MS = 20_000

export function useUploadQueue(today: ISODate | undefined): UploadQueueView {
  const session = useSession()
  const userId = session.status === 'signed-in' ? session.session.user.id : null

  const [entries, setEntries] = useState<QueueEntry[]>([])
  const [ready, setReady] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const store = useRef<QueueStore | null>(null)
  /**
   * One pass at a time inside this tab, before the cross-tab lock is even consulted — and a pass
   * asked for mid-pass runs right after it instead of being dropped (lib/upload-queue.ts).
   */
  const [gate] = useState(createPassGate)

  const refresh = useCallback(async () => {
    if (!store.current) return
    setEntries(await store.current.all())
  }, [])

  // Open the phone's store once. Demo mode never does: there is no upload path to queue for, and
  // a database created by a demo visit would outlive the visit.
  useEffect(() => {
    if (isDemoMode) return
    let live = true
    void openQueueStore()
      .then(async (opened) => {
        if (!live) {
          opened.close()
          return
        }
        store.current = opened
        setReady(true)
        setEntries(await opened.all())
      })
      .catch(() => {
        if (live) setNote(STORAGE_REFUSED)
      })
    return () => {
      live = false
      store.current?.close()
      store.current = null
    }
  }, [])

  const pass = useCallback(async () => {
    if (!store.current) return
    await gate.request(async () => {
      // Read at run time: a follow-up pass can start after the store was closed on unmount.
      const open = store.current
      if (!open) return
      // IMPORT BEFORE SENDING, always. A share that arrived while the app was closed has to
      // become a durable entry before anything tries to upload it, or a crash mid-send loses it.
      let refusal: string | null = null
      if (userId && today) {
        const staged = await listStagedShares()
        if (staged.length > 0) {
          const outcomes = await importStagedShares({ store: open, shares: staged, userId, date: today })
          refusal = outcomes.find((outcome) => outcome.refused !== null)?.refused ?? null
        }
      }
      const result = await runQueueOnce({ store: open, api: data, userId })
      // Paused is worth saying out loud, and so is a refused write — both mean a recording is
      // somewhere the queue cannot act on. Offline is neither: the row already says the recording
      // is here and waiting, which is the whole truth.
      setNote(result.paused ?? refusal)
      await refresh()
    })
  }, [gate, refresh, today, userId])

  // App open, back online, and a visible-tab interval. Three triggers, no promises about a
  // closed app: nothing here runs when the PWA is not running.
  useEffect(() => {
    if (isDemoMode || !ready) return
    void pass()
    const onOnline = () => void pass()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void pass()
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void pass()
    }, QUEUE_INTERVAL_MS)
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pass, ready])

  const retry = useCallback(
    (id: string) => {
      const open = store.current
      if (!open) return
      void retryNow(open, id)
        .then(refresh)
        .then(() => pass())
    },
    [pass, refresh],
  )

  const remove = useCallback(
    (id: string) => {
      const open = store.current
      if (!open) return
      void removeEntry(open, id).then(refresh)
    },
    [refresh],
  )

  const sendAsNew = useCallback(
    (id: string) => {
      const open = store.current
      if (!open) return
      void sendAsNewUpload(open, id)
        .then(refresh)
        .then(() => pass())
    },
    [pass, refresh],
  )

  const importNative = useCallback(
    async (input: NativeImport): Promise<ImportOutcome> => {
      const open = store.current
      if (!open || !userId || !today) {
        return {
          stageId: input.path,
          entry: null,
          created: false,
          // Not an error and not a refusal by the phone: the queue simply is not open for
          // business, and saying WHICH is what lets the owner act (sign in, or wait a beat).
          // Demo mode gets its own sentence because it is not a signed-out account — it is an
          // app with no R2 and no desktop PC behind it, which is a different fact.
          refused: isDemoMode ? NO_QUEUE_IN_DEMO : !userId ? SIGNED_OUT_NOTE : STORAGE_REFUSED,
        }
      }
      const outcome = await importNativeRecording({
        store: open,
        userId,
        date: input.date ?? today,
        path: input.path,
        courseCode: input.courseCode,
        blob: input.blob,
        durationSec: input.durationSec ?? null,
        title: input.title ?? null,
      })
      await refresh()
      if (outcome.entry) void pass()
      return outcome
    },
    [pass, refresh, today, userId],
  )

  return { entries, ready, note, retry, remove, sendAsNew, importNative }
}

/**
 * File a recording under a course — or unfile it with `null`.
 *
 * Both caches are invalidated, because the same lecture is on two screens at once (the list on
 * `/record` and its own page) and a filed recording that still reads "No course" on the other one
 * is the app disagreeing with itself.
 */
export function useAssignLectureCourse() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, courseCode }: { id: string; courseCode: string | null }) =>
      data.assignLectureCourse(id, courseCode),
    onSuccess: (lecture) => {
      client.setQueryData(['capturedLecture', lecture.id], lecture)
      void client.invalidateQueries({ queryKey: ['capturedLectures'] })
    },
  })
}

// ── school · the three School screens (6a/6b/6c) ────────────────────────────

/** One course by its URL slug ('cogsci-175'). `null` data means no such course, not a failure. */
export function useCourse(slug: string | undefined) {
  return useQuery({
    queryKey: ['course', slug],
    queryFn: () => data.getCourse(slug!),
    enabled: Boolean(slug),
  })
}

export function useAnnouncements(filter?: AnnouncementFilter) {
  return useQuery({
    queryKey: ['announcements', filter],
    queryFn: () => data.getAnnouncements(filter),
  })
}

/**
 * Marking announcements read — what expanding one in the School feed does.
 *
 * OPTIMISTIC, and it has to be: the dot sits under the row the owner just clicked, and a dot that
 * takes a round trip to go out reads as a click that did not land. The cache is patched in
 * `onMutate`, rolled back verbatim if the write fails (the dot comes BACK, which is the honest
 * outcome — the row is still unread), and the course badges are invalidated on settle so the
 * count and the list cannot disagree.
 *
 * Only the unread ones are ever sent. `read` is a latch, so re-marking a read row is a no-op the
 * server would honestly report as zero — but it is still a request, and the feed would make one
 * per expand forever.
 */
export function useMarkAnnouncementsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => data.markAnnouncementsRead(ids),
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: ['announcements'] })
      const previous = qc.getQueriesData<Announcement[]>({ queryKey: ['announcements'] })
      const marked = new Set(ids)
      for (const [key] of previous) {
        qc.setQueryData<Announcement[]>(key, (rows) =>
          rows?.map((row) => (marked.has(row.id) ? { ...row, read: true } : row)),
        )
      }
      return { previous }
    },
    onError: (_err, _ids, context) => {
      for (const [key, rows] of context?.previous ?? []) qc.setQueryData(key, rows)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['announcements'] })
      // The badge on every course card is a count of these rows. Leaving it alone would leave
      // "2 unread" over a list with one dot in it.
      void qc.invalidateQueries({ queryKey: ['courses'] })
      void qc.invalidateQueries({ queryKey: ['course'] })
    },
  })
}

/** The syllabus panel. `null` is a real answer — no PDF has been read for this course. */
export function useSyllabus(courseId: string | undefined) {
  return useQuery({
    queryKey: ['syllabus', courseId],
    queryFn: () => data.getSyllabus(courseId!),
    enabled: Boolean(courseId),
  })
}

/**
 * THE SYLLABUS FILE the Canvas connector cataloged, and its text.
 *
 * Its own query key rather than a field on `['syllabus', …]`: the two answer different questions
 * from different tables (`course_files` versus `syllabus_extracts`), and generating an extract
 * must not invalidate a PDF reading that no model was involved in.
 */
export function useSyllabusDocument(courseId: string | undefined) {
  return useQuery({
    queryKey: ['syllabus-document', courseId],
    queryFn: () => data.getSyllabusDocument(courseId!),
    enabled: Boolean(courseId),
  })
}

/**
 * Read this course's Canvas syllabus now.
 *
 * The panel's read is invalidated on success rather than written from the mutation's answer:
 * one round trip after the extraction is cheap, and it means what the panel shows is what the
 * GET says — there is no path where a stored row and a rendered one disagree.
 */
export function useGenerateSyllabus() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (courseId: string) => data.generateSyllabus(courseId),
    onSuccess: (_extract, courseId) => {
      void client.invalidateQueries({ queryKey: ['syllabus', courseId] })
    },
  })
}

export function useLectureSummary(lectureId: string | undefined) {
  return useQuery({
    queryKey: ['lectureSummary', lectureId],
    queryFn: () => data.getLectureSummary(lectureId!),
    enabled: Boolean(lectureId),
  })
}

export function useFlashcards(lectureId: string | undefined) {
  return useQuery({
    queryKey: ['flashcards', lectureId],
    queryFn: () => data.getFlashcards(lectureId!),
    enabled: Boolean(lectureId),
  })
}

export function usePracticeQuestions(lectureId: string | undefined) {
  return useQuery({
    queryKey: ['practiceQuestions', lectureId],
    queryFn: () => data.getPracticeQuestions(lectureId!),
    enabled: Boolean(lectureId),
  })
}

/**
 * Generate the study set. ONE mutation invalidating THREE reads, because one model call makes all
 * three artifacts and they share one provenance — refreshing only the panel that asked would let
 * the other two keep showing an older reading beside the new one.
 */
export function useGenerateStudySet() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (lectureId: string) => data.generateStudySet(lectureId),
    onSuccess: (_set, lectureId) => {
      for (const key of ['lectureSummary', 'flashcards', 'practiceQuestions']) {
        void client.invalidateQueries({ queryKey: [key, lectureId] })
      }
    },
  })
}

// ── tasks ───────────────────────────────────────────────────────────────────

export function useTasks(filter?: TaskFilter) {
  return useQuery({ queryKey: tasksKey(filter), queryFn: () => data.getTasks(filter) })
}

/**
 * A REMINDER IS AN AMBIENT TASK, and this is the one place that sentence is code.
 *
 * `/api/tasks/reminders` selects `reminder_at is not null OR due is null` — the demo
 * implementation filters on exactly the same two fields (demo.ts → `getReminders`). Anything
 * that satisfies it belongs in the Reminders card the moment it is typed, so the optimistic echo
 * below asks the same question rather than a second, drifting version of it.
 */
function isAmbient(input: TaskInput): boolean {
  return (input.reminderAt ?? null) !== null || (input.due ?? null) === null
}

/**
 * CAN THIS WRITE CHANGE WHAT THE PHONE RINGS? — the question every write below asks before it
 * calls `armPhoneAlarmsNow` (lib/phone-alarms.ts) on success.
 *
 * For a write that names only an id (tick, delete, an edit that does not touch `reminderAt`), the
 * answer is on the cached row: a task with a reminder, or a repeating one (ticking it writes the
 * next occurrence, reminder and all). A row in no cache at all is UNKNOWN, and unknown asks the
 * phone — one extra check is cheap, a reminder that rings after it was deleted is not. The lookup
 * runs in `onMutate`, before the refetch that follows the write can take the row away.
 */
function cachedTaskMayRing(qc: QueryClient, id: string): boolean {
  let seen = false
  for (const [, rows] of qc.getQueriesData<Task[]>({ queryKey: ['tasks'] })) {
    const hit = Array.isArray(rows) ? rows.find((t) => t.id === id) : undefined
    if (hit) {
      seen = true
      if (hit.reminderAt || hit.rrule) return true
    }
  }
  const reminders = qc.getQueryData<Task[]>(remindersKey)
  const hit = Array.isArray(reminders) ? reminders.find((t) => t.id === id) : undefined
  if (hit) {
    seen = true
    if (hit.reminderAt || hit.rrule) return true
  }
  return !seen
}

/** The same question for a calendar block: one with reminder offsets, or one no cache holds. */
function cachedBlockMayRing(qc: QueryClient, id: string): boolean {
  let seen = false
  for (const [, range] of qc.getQueriesData<CalendarRange>({ queryKey: ['calendar'] })) {
    const hit = range?.blocks?.find((b) => b.id === id)
    if (hit) {
      seen = true
      if ((hit.reminders ?? []).length > 0) return true
    }
  }
  return !seen
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TaskInput) => data.createTask(input),
    // OPTIMISTIC, because the card the row lands in is on screen while it is being typed. The
    // temporary row carries only what was actually typed: no estimate, no priority, no invented
    // date. `createdAt` is the browser's clock and is replaced by the server's on the refetch —
    // the card reads it for the "N days old" stamp, and a missing one would render as 1970.
    onMutate: async (input: TaskInput) => {
      if (!isAmbient(input)) return { previous: undefined }
      await qc.cancelQueries({ queryKey: remindersKey })
      const previous = qc.getQueryData<Task[]>(remindersKey)
      const optimistic: Task = {
        id: `optimistic-${crypto.randomUUID()}`,
        title: input.title,
        done: false,
        due: input.due ?? null,
        list: input.list ?? 'Inbox',
        priority: 'none',
        rrule: null,
        reminderAt: input.reminderAt ?? null,
        createdAt: new Date().toISOString(),
        completedAt: null,
        estimateMin: input.estimateMin ?? null,
        // Typed into the quick-add box, so nothing proposed it.
        proposalId: null,
      }
      qc.setQueryData<Task[]>(remindersKey, (rows) => [optimistic, ...(rows ?? [])])
      return { previous }
    },
    // The row goes back out. It was never saved, and a reminder that looks saved and is not is
    // the failure this whole card exists to avoid.
    onError: (_err, _input, context) => {
      if (context?.previous !== undefined) qc.setQueryData(remindersKey, context.previous)
    },
    onSuccess: (_task, input) => {
      if ((input.reminderAt ?? null) !== null) armPhoneAlarmsNow()
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: remindersKey })
    },
  })
}

export function useToggleTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => data.toggleTask(id, done),
    onMutate: ({ id }: { id: string; done: boolean }) => ({ mayRing: cachedTaskMayRing(qc, id) }),
    onSuccess: (_result, _vars, context) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: ['reminders'] })
      if (context?.mayRing) armPhoneAlarmsNow()
    },
  })
}

/**
 * SET OR CLEAR A DUE DATE, MOVE A LIST — the row menu's two writes.
 *
 * Not optimistic, deliberately. Changing a due date moves the row between cards on a board whose
 * every group is derived (components/tasks/derive.ts); guessing where it lands and then being
 * corrected by the refetch is a row that jumps twice. The refetch is one round trip and the row
 * moves once, to the right place.
 */
export function useUpdateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }) => data.updateTask(id, patch),
    // Setting or clearing the time always counts; any other edit counts when the row already rings
    // (a new title is the words on the notification, a new day moves it).
    onMutate: ({ id, patch }: { id: string; patch: TaskPatch }) => ({
      mayRing: 'reminderAt' in patch || cachedTaskMayRing(qc, id),
    }),
    onSuccess: (_result, _vars, context) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: remindersKey })
      if (context?.mayRing) armPhoneAlarmsNow()
    },
  })
}

/** Delete. Not complete — a completion is a fact worth keeping, and this is for the other kind. */
export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.deleteTask(id),
    // A deleted reminder must stop ringing on the phone as promptly as a new one starts.
    onMutate: (id: string) => ({ mayRing: cachedTaskMayRing(qc, id) }),
    onSuccess: (_result, _id, context) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: remindersKey })
      if (context?.mayRing) armPhoneAlarmsNow()
    },
  })
}

export function useReminders() {
  return useQuery({ queryKey: remindersKey, queryFn: () => data.getReminders() })
}

// ── calendar (11a/11b) ──────────────────────────────────────────────────────
//
// ONE READ HOOK FOR BOTH VIEWS. Day and week ask for different ranges of the same endpoint
// rather than having a hook each — two hooks would mean two caches of overlapping days, and
// then a block moved in day view would still be in its old place when the week reloaded.
//
// EVERY WRITE INVALIDATES `['calendar']` WHOLESALE rather than patching the cache by hand. A
// block that moves across midnight changes which range it belongs to, and a hand-patched cache
// is exactly where a block ends up on two days at once. `['tasks']` goes with it because
// dragging a task onto the grid changes what "unscheduled" means.

export function useCalendarRange(range: DateRange | undefined) {
  return useQuery({
    queryKey: calendarKey(range),
    queryFn: () => data.getCalendarRange(range!),
    enabled: Boolean(range),
  })
}

function useCalendarInvalidation() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    void qc.invalidateQueries({ queryKey: ['tasks'] })
  }
}

export function useCreateCalendarBlock() {
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (input: CalendarBlockInput) => data.createCalendarBlock(input),
    onSuccess: (_block, input) => {
      invalidate()
      if ((input.reminders ?? []).length > 0) armPhoneAlarmsNow()
    },
  })
}

/**
 * The local wall clock a timestamp SAYS, read in the browser's own zone.
 *
 * FOR THE OPTIMISTIC ECHO ONLY. The authoritative `date` / `startMinutes` / `endMinutes` are
 * derived on the Worker in the deployment's timezone and arrive with the refetch that follows
 * every write; this is the same arithmetic run locally so the grid does not have to wait a round
 * trip to redraw a block the owner just dragged. It is exact for the timestamps this app writes,
 * which carry the browser's own offset (see `localTimestamp` in routes/Calendar.tsx).
 */
function wallClockOf(timestamp: string): { date: ISODate; minutes: number } {
  const at = new Date(timestamp)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` as ISODate,
    minutes: at.getHours() * 60 + at.getMinutes(),
  }
}

/** The patch applied to a cached block, derived fields and all — what the server will echo back. */
function echoPatch(block: CalendarBlock, patch: CalendarBlockPatch): CalendarBlock {
  const startsAt = patch.startsAt ?? block.startsAt
  const endsAt = patch.endsAt ?? block.endsAt
  const start = wallClockOf(startsAt)
  return {
    ...block,
    title: patch.title ?? block.title,
    source: patch.source ?? block.source,
    state: patch.state ?? block.state,
    startsAt,
    endsAt,
    date: start.date,
    startMinutes: start.minutes,
    endMinutes: wallClockOf(endsAt).minutes,
    location: patch.location !== undefined ? patch.location : block.location,
    taskId: patch.taskId !== undefined ? patch.taskId : block.taskId,
  }
}

/**
 * A block edit, applied to the grid BEFORE the server has agreed to it.
 *
 * WHY OPTIMISTIC HERE AND NOWHERE ELSE IN THIS FILE. Every other write on this screen is a form
 * submission with a Saving… state to look at. A drag has no such moment: the pointer comes up and
 * the block has either moved or it has not, and a block that sits still for 200ms and then jumps
 * reads as a dropped gesture. So the cache is patched on release and RESTORED WHOLESALE if the
 * write fails — every `['calendar', …]` entry, not just the visible range, because a block that
 * moved across midnight is in two ranges at once and a half-rolled-back cache would leave it in
 * both. The Calendar screen says out loud when a rollback happened; a silent snap-back would be
 * the screen hiding a failure, which is the one thing this app is not allowed to do.
 */
export function useUpdateCalendarBlock() {
  const qc = useQueryClient()
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CalendarBlockPatch }) =>
      data.updateCalendarBlock(id, patch),
    onMutate: async ({ id, patch }: { id: string; patch: CalendarBlockPatch }) => {
      // Read BEFORE the optimistic patch below: a changed offset list always counts, and a move
      // or rename counts when the block already carries offsets (its alarms moved with it).
      const mayRing = patch.reminders !== undefined || cachedBlockMayRing(qc, id)
      // In-flight reads would land on top of the optimistic write and undo it mid-drag.
      await qc.cancelQueries({ queryKey: ['calendar'] })
      const previous = qc.getQueriesData<CalendarRange>({ queryKey: ['calendar'] })
      qc.setQueriesData<CalendarRange>({ queryKey: ['calendar'] }, (range) =>
        range
          ? {
              ...range,
              blocks: range.blocks.map((b) => (b.id === id ? echoPatch(b, patch) : b)),
            }
          : range,
      )
      return { previous, mayRing }
    },
    onError: (_error, _variables, context) => {
      for (const [key, snapshot] of context?.previous ?? []) qc.setQueryData(key, snapshot)
    },
    onSuccess: (_block, _variables, context) => {
      if (context?.mayRing) armPhoneAlarmsNow()
    },
    // ON SETTLED, NOT ON SUCCESS: the refetch has to run after a rollback too, or a cache restored
    // from a snapshot stays the newest thing the screen knows about a week it just failed to write.
    onSettled: invalidate,
  })
}

export function useDeleteCalendarBlock() {
  const qc = useQueryClient()
  const invalidate = useCalendarInvalidation()
  return useMutation({
    mutationFn: (id: string) => data.deleteCalendarBlock(id),
    onMutate: (id: string) => ({ mayRing: cachedBlockMayRing(qc, id) }),
    onSuccess: (_result, _id, context) => {
      invalidate()
      if (context?.mayRing) armPhoneAlarmsNow()
    },
  })
}

// ── learning ────────────────────────────────────────────────────────────────

export function useLearningTracks() {
  return useQuery({ queryKey: ['learningTracks'], queryFn: () => data.getLearningTracks() })
}

export function useLearningItems(date: ISODate | undefined) {
  return useQuery({
    queryKey: learningItemsKey(date),
    queryFn: () => data.getLearningItems(date!),
    enabled: Boolean(date),
  })
}

export function useToggleLearningItem() {
  const qc = useQueryClient()
  return useMutation({
    // `session` is optional and only read when checking ON: minutes and a note belong to work that
    // happened, and unchecking is the deletion of the record that it did.
    mutationFn: ({
      id,
      done,
      session,
    }: {
      id: string
      done: boolean
      session?: LearningSessionInput
    }) => data.toggleLearningItem(id, done, session),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['learningItems'] })
      void qc.invalidateQueries({ queryKey: ['learningTracks'] })
      void qc.invalidateQueries({ queryKey: ['learningStreak'] })
      // Checking an item off moves the percentage, the pace phrase, the current unit and the week
      // strip at once. A screen that refreshed one of them and not the others would be showing two
      // answers to the same question, which is the whole thing this port is against.
      void qc.invalidateQueries({ queryKey: ['learningTrackSummaries'] })
      void qc.invalidateQueries({ queryKey: ['learningTrackDetail'] })
      // The library counts how many items behind each link are still open, so it moves too.
      void qc.invalidateQueries({ queryKey: learningResourcesKey })
    },
  })
}

export function useLearningStreak() {
  return useQuery({ queryKey: ['learningStreak'], queryFn: () => data.getLearningStreak() })
}

// ── learning · the two Learning screens (9a/9b) ─────────────────────────────
//
// `retry: false` on both, for the same reason the Connections reads carry it: they throw
// NotImplementedError in remote mode BY DESIGN (V3), and retrying a named absence three times only
// delays the screen saying so.

/** Both track cards on `/learning`, pace and today's item already derived by the contract. */
export function useLearningTrackSummaries() {
  return useQuery({
    queryKey: learningTrackSummariesKey,
    queryFn: () => data.getLearningTrackSummaries(),
    retry: false,
  })
}

/** One track page. `null` data means no such track, which is not a failure. */
export function useLearningTrackDetail(trackId: string | undefined) {
  return useQuery({
    queryKey: ['learningTrackDetail', trackId],
    queryFn: () => data.getLearningTrackDetail(trackId!),
    enabled: Boolean(trackId),
    retry: false,
  })
}

/** The Resources section: every link the plans point at, per track, deduplicated by the Worker. */
export function useLearningResources() {
  return useQuery({
    queryKey: learningResourcesKey,
    queryFn: () => data.getLearningResources(),
    retry: false,
  })
}

/**
 * The markdown export, for pasting into an AI outside the app.
 *
 * A MUTATION rather than a query, and the distinction is not a technicality: this is the whole
 * curriculum, nothing on the screen renders it, and it is fetched only when the owner presses the
 * button. A `useQuery` would either run on mount (paying for a page nobody asked for) or sit
 * disabled with a flag to fake the press. `mutateAsync` also hands the caller the string directly,
 * which is what the clipboard write needs — and `retry: false` because a failed copy should say
 * so immediately rather than three attempts later.
 */
export function useExportLearning() {
  return useMutation({ mutationFn: () => data.exportLearning(), retry: false })
}

// ── interests ───────────────────────────────────────────────────────────────
//
// `retry: false` on all four, for the reason the Connections hooks give: these throw
// NotImplementedError in remote mode BY DESIGN, and retrying a named absence three times only
// delays the screen saying so.

export function useInterestSaves(filter?: SaveFilter) {
  return useQuery({
    queryKey: ['interestSaves', filter],
    queryFn: () => data.getInterestSaves(filter),
    retry: false,
  })
}

/** The nightly clusters, most recently added to first. */
export function useInterestTopics() {
  return useQuery({
    queryKey: ['interestTopics'],
    queryFn: () => data.getInterestTopics(),
    retry: false,
  })
}

/** The header band's counts: library size, inbox size, backlog import. */
export function useInterestsOverview() {
  return useQuery({
    queryKey: ['interestsOverview'],
    queryFn: () => data.getInterestsOverview(),
    retry: false,
  })
}

/** One save with its nearest neighbours — the right-hand detail pane. */
export function useInterestSave(id: string | undefined) {
  return useQuery({
    queryKey: ['interestSave', id],
    queryFn: () => data.getInterestSave(id!),
    enabled: Boolean(id),
    retry: false,
  })
}

// ── connections (settings · the trust surface) ───────────────────────────────
//
// The scenario is part of every key: switching review states must refetch, and
// two states sharing a cache entry is exactly the kind of stale screenshot this
// screen exists to make impossible.
//
// `retry: false` on all four. These reads throw NotImplementedError in remote
// mode BY DESIGN, and retrying a named absence three times only delays the
// screen saying so.

export function useConnectors(scenario?: ConnectionsScenario) {
  return useQuery({
    queryKey: ['connectors', scenario],
    queryFn: () => data.getConnectors(scenario),
    retry: false,
  })
}

export function useDesktopWorker(scenario?: ConnectionsScenario) {
  return useQuery({
    queryKey: ['desktopWorker', scenario],
    queryFn: () => data.getDesktopWorker(scenario),
    retry: false,
  })
}

export function useCaptureTokens(scenario?: ConnectionsScenario) {
  return useQuery({
    queryKey: ['captureTokens', scenario],
    queryFn: () => data.getCaptureTokens(scenario),
    retry: false,
  })
}

export function useDataStatus(scenario?: ConnectionsScenario) {
  return useQuery({
    queryKey: ['dataStatus', scenario],
    queryFn: () => data.getDataStatus(scenario),
    retry: false,
  })
}

// ── mail · Google accounts for read-only Gmail (Day page, Lane 1a) ──────────
//
// The account list is its own read, beside `connectors`, because the card needs ids to disconnect
// and the connector shape (rightly) carries none. Both are invalidated together after every write,
// so the card's scope line and its account list cannot disagree.

export const mailAccountsKey = ['mailAccounts'] as const

export function useMailAccounts() {
  return useQuery({ queryKey: mailAccountsKey, queryFn: () => data.listMailAccounts(), retry: false })
}

function useMailInvalidation() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: mailAccountsKey })
    void qc.invalidateQueries({ queryKey: ['connectors'] })
  }
}

/**
 * Ask the Worker where to send the browser. The CALLER does the leaving (the Gmail card): a full
 * URL is Google's consent screen and gets `window.location.assign`; demo mode answers an in-app
 * path, which is navigated to so the in-memory demo account survives.
 */
export function useStartGoogleMailConnect() {
  const invalidate = useMailInvalidation()
  return useMutation({
    mutationFn: () => data.startGoogleMailConnect(),
    // Remote mode leaves the page before this matters; demo mode has already added the account.
    onSuccess: invalidate,
  })
}

export function useDisconnectMailAccount() {
  const invalidate = useMailInvalidation()
  return useMutation({
    mutationFn: (id: string) => data.disconnectMailAccount(id),
    onSuccess: invalidate,
  })
}

/**
 * A connector's on-demand pull. On success it invalidates the sync-status row the card itself
 * reads (`syncStatus`, `connectors`) and every School query the sync just wrote
 * (`courses`/`course`/`assignments`/`announcements`) — the same tables
 * `POST /api/connections/canvas/sync` upserts — so the header chip and the School screen catch up
 * without a manual refresh.
 */
export function useSyncConnector() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.syncConnector(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['syncStatus'] })
      void qc.invalidateQueries({ queryKey: ['connectors'] })
      void qc.invalidateQueries({ queryKey: ['courses'] })
      void qc.invalidateQueries({ queryKey: ['course'] })
      void qc.invalidateQueries({ queryKey: ['assignments'] })
      void qc.invalidateQueries({ queryKey: ['announcements'] })
      // Gmail's pull (Lane 1b) writes each account's last sync and error, and the messages.
      void qc.invalidateQueries({ queryKey: mailAccountsKey })
      void qc.invalidateQueries({ queryKey: ['mailMessages'] })
      void qc.invalidateQueries({ queryKey: mailTriageKey })
    },
  })
}

// ── mail · triage, grading and rules (Day page prototype) ───────────────────
//
// ONE READ, AND EVERY WRITE RE-READS IT. A move, a done or a rule changes which pile (and which
// count) a message belongs to, and the Worker owns that grouping — so the page asks again rather
// than re-deriving the piles in the browser.

export const mailTriageKey = ['mailTriage'] as const
export const mailRulesKey = ['mailRules'] as const

export function useMailTriage() {
  return useQuery({ queryKey: mailTriageKey, queryFn: () => data.getMailTriage(), retry: false })
}

/**
 * THE DESIGNED DAY PAGE'S READ: the same endpoint with `?days=7`. Its key sits UNDER
 * `mailTriageKey`, so every write's `invalidateQueries({ queryKey: mailTriageKey })` refreshes it
 * too — the prototype and the designed page can never disagree about a message.
 *
 * Polled every five minutes while the page is open: the sync is the Worker's 30-minute cron, and a
 * page left open all morning should catch up without a reload (14e — new mail waits behind a pill).
 */
export function dayTriageKey(query: MailTriageQuery) {
  return [...mailTriageKey, 'day', query.days ?? null, query.scenario ?? null] as const
}

export function useDayTriage(query: MailTriageQuery) {
  return useQuery({
    queryKey: dayTriageKey(query),
    queryFn: () => data.getMailTriage(query),
    retry: false,
    refetchInterval: 5 * 60_000,
  })
}

export const mailAuditKey = (weekStart: ISODate | undefined, scenario?: DayScenario | null) =>
  ['mailAudit', weekStart ?? null, scenario ?? null] as const

/** The weekly check for the week starting `weekStart` (a Monday). */
export function useMailAudit(weekStart: ISODate | undefined, scenario?: DayScenario | null) {
  return useQuery({
    queryKey: mailAuditKey(weekStart, scenario),
    queryFn: () => data.getMailAudit(weekStart!, scenario ?? undefined),
    enabled: Boolean(weekStart),
    retry: false,
  })
}

function useTriageInvalidation() {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: mailTriageKey }),
      // Grading a weekly-check item IS a verdict call, so the check re-reads with everything else.
      qc.invalidateQueries({ queryKey: ['mailAudit'] }),
    ])
}

export function useSetMailVerdict() {
  const invalidate = useTriageInvalidation()
  return useMutation({
    mutationFn: (v: { id: string; verdict: MailVerdict; reason?: MailCorrectionReason }) =>
      data.setMailVerdict(v.id, v.verdict, v.reason),
    onSuccess: invalidate,
  })
}

export function useSetMailState() {
  const invalidate = useTriageInvalidation()
  return useMutation({
    mutationFn: (v: { id: string; state: MailItemState; snoozedUntil?: string }) =>
      data.setMailState(v.id, v.state, v.snoozedUntil),
    onSuccess: invalidate,
  })
}

export function useMailRules() {
  return useQuery({ queryKey: mailRulesKey, queryFn: () => data.listMailRules(), retry: false })
}

export function useCreateMailRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MailRuleInput) => data.createMailRule(input),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: mailRulesKey }),
        qc.invalidateQueries({ queryKey: mailTriageKey }),
      ])
    },
  })
}

export function useDeleteMailRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.deleteMailRule(id),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: mailRulesKey }),
        qc.invalidateQueries({ queryKey: mailTriageKey }),
      ])
    },
  })
}

export function useRunMailTriage() {
  const invalidate = useTriageInvalidation()
  return useMutation({
    mutationFn: (limit?: number) => data.runMailTriage(limit),
    onSuccess: invalidate,
  })
}

// ── mail · next steps (Day page, Lane 6) ────────────────────────────────────
//
// An accept can write a LifeOS calendar block or reminder (and an undo deletes it), so the
// calendar and the reminders re-read with the triage. The designed Day page drives these through
// its controller (optimistic, with the undo toast); these hooks are the same calls for any other
// screen.

/** Every read a proposal decision can change. */
export function invalidateAfterProposal(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: mailTriageKey }),
    qc.invalidateQueries({ queryKey: ['calendar'] }),
    qc.invalidateQueries({ queryKey: ['tasks'] }),
    qc.invalidateQueries({ queryKey: ['reminders'] }),
  ])
}

export function useAcceptMailProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.acceptMailProposal(id),
    onSettled: () => invalidateAfterProposal(qc),
  })
}

export function useSkipMailProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.skipMailProposal(id),
    onSettled: () => invalidateAfterProposal(qc),
  })
}

export function useUndoMailProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => data.undoMailProposal(id),
    onSettled: () => invalidateAfterProposal(qc),
  })
}

/**
 * What the mail sync has read — one page, newest first, with the Worker's filters. The key carries
 * the whole filter, so two pages (or two accounts) are two cache entries, never one overwritten.
 */
export function useMailMessages(filter: MailMessageFilter = {}) {
  return useQuery({
    queryKey: ['mailMessages', filter],
    queryFn: () => data.listMailMessages(filter),
    retry: false,
  })
}

// ── search (10a/10b) ────────────────────────────────────────────────────────

/**
 * One search across five domains.
 *
 * `enabled` on a non-empty query is what makes the empty screen empty: with no query there is
 * nothing to ask, and a request for '' would come back with either everything or nothing, both
 * of which are answers to a question nobody asked.
 *
 * DEBOUNCING IS THE CALLER'S JOB (README §Interactions → Search: "Debounce ~200 ms"), and it
 * belongs there rather than here: the URL is the query's home, and what is debounced is how often
 * the URL changes, not how often the cache is consulted. `keepPreviousData` is deliberately NOT
 * set — README §Loading says stale content stays visible and is LABELLED stale, and there is no
 * honest label for "these are the answers to the last thing you typed".
 *
 * LIVE since V4 (`remote.ts` → `search`, RRF fusion over Postgres FTS and Vectorize). `retry:
 * false` still applies: a genuine failure here is worth surfacing immediately rather than
 * retried silently three times while the screen sits on stale results.
 */
export function useSearch(query: string) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: ['search', trimmed],
    queryFn: () => data.search(trimmed),
    enabled: trimmed.length > 0,
    retry: false,
  })
}
// ── chat (11a/11b) and the ⌘K capture palette (11c) ─────────────────────────
//
// BOTH LIVE — chat since V4, capture (`POST /api/capture/quick`) since V5. `retry: false`
// throughout is what it always was: a real failure is worth surfacing immediately rather than
// retried silently three times first.

/** Every thread, newest activity first. The list column does the TODAY/THIS WEEK/EARLIER grouping. */
export function useChatThreads() {
  return useQuery({ queryKey: ['chatThreads'], queryFn: () => data.getChatThreads(), retry: false })
}

/** One thread opened. `null` data means no thread with that id, which is not a failure. */
export function useChatThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ['chatThread', threadId],
    queryFn: () => data.getChatThread(threadId!),
    enabled: Boolean(threadId),
    retry: false,
  })
}

/**
 * Every scope pill with its own derived size. One read for the whole row: the sizes have to agree
 * with each other and with the answer that cites them, so they come out of one derivation rather
 * than one request per pill.
 */
export function useChatScopes() {
  return useQuery({ queryKey: ['chatScopes'], queryFn: () => data.getChatScopes(), retry: false })
}

/** The three starter cards on an empty thread, each already carrying what it would read. */
export function useChatStarters() {
  return useQuery({
    queryKey: ['chatStarters'],
    queryFn: () => data.getChatStarters(),
    retry: false,
  })
}

/**
 * ASK — the one write on the Chat screen (V4).
 *
 * `retry: false`, and here it is not the usual reason. Every other hook in this file turns retries
 * off because a named absence should be reported once rather than three times; this one turns them
 * off because ASKING IS NOT IDEMPOTENT. A retried question is a second question in the thread and
 * a second call to a paid model.
 *
 * It invalidates the thread list and the one thread it landed in — and `chatScopes`, because a new
 * thread does not move a scope size but the same corpus counted a moment later can.
 */
export function useSendChatMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ChatSendInput) => data.sendChatMessage(input),
    retry: false,
    onSuccess: (result) => {
      // THE ANSWER IS ON SCREEN THE MOMENT IT LANDS. The screens show the question in the
      // conversation while it is being answered (owner, 2026-09-23); if the thread then had to
      // wait for a refetch, the question would blink out between the two. So the two new messages
      // go into the thread's cache first, and the refetch only confirms them.
      qc.setQueryData<ChatThreadDetail | null>(['chatThread', result.thread.id], (old) => {
        const have = new Set((old?.messages ?? []).map((m) => m.id))
        return {
          thread: result.thread,
          messages: [...(old?.messages ?? []), ...result.messages.filter((m) => !have.has(m.id))],
        }
      })
      void qc.invalidateQueries({ queryKey: ['chatThreads'] })
      void qc.invalidateQueries({ queryKey: ['chatThread', result.thread.id] })
    },
  })
}

/**
 * REMOVE A THREAD — the thread list's right-click menu.
 *
 * It removes the thread's own cache entry as well as invalidating the list, because a refetch of
 * `['chatThread', id]` after the row is gone is a request whose only possible answer is a 404 the
 * screen would have to render as a failure. `chatScopes` is left alone: a scope's size counts the
 * corpus a thread reads FROM, and deleting the conversation does not unwrite the journal.
 */
export function useDeleteChatThread() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (threadId: string) => data.deleteChatThread(threadId),
    retry: false,
    onSuccess: (thread) => {
      qc.removeQueries({ queryKey: ['chatThread', thread.id] })
      void qc.invalidateQueries({ queryKey: ['chatThreads'] })
    },
  })
}

/**
 * SEND A PICTURE UP, so the next question can be about it.
 *
 * A MUTATION AND NOT PART OF `useSendChatMessage`, because the two fail at different moments and
 * for different reasons: an image that is too big should say so while the composer is still open
 * and the question half typed, not when the question is submitted. It invalidates nothing — an
 * unattached upload is not on any screen yet.
 */
export function useUploadChatAttachment() {
  return useMutation({
    mutationFn: (file: Blob): Promise<ChatAttachment> => data.uploadChatAttachment(file),
    retry: false,
  })
}

/**
 * An object URL for one already-sent picture.
 *
 * `staleTime: Infinity` and no refetch: the bytes behind an id never change, and re-fetching them
 * on every window focus would re-download a screenshot to draw the same thumbnail.
 */
export function useChatAttachmentUrl(attachmentId: string | undefined) {
  return useQuery({
    queryKey: ['chatAttachment', attachmentId],
    queryFn: () => data.getChatAttachmentUrl(attachmentId!),
    enabled: Boolean(attachmentId),
    retry: false,
    staleTime: Infinity,
  })
}

/**
 * ACCEPT A PROPOSAL — the one write that turns a suggestion into a row.
 *
 * `retry: false`, and here for the sharpest version of the reason `useSendChatMessage` gives:
 * ACCEPTING IS NOT IDEMPOTENT FROM THE CLIENT'S SIDE. The Worker refuses a second accept with a
 * 409, so a retry cannot actually create two blocks — but it would turn a network blip into an
 * error message about something that had already worked, which is worse than the blip.
 *
 * It invalidates the thread (the card's own status changed), the calendar and the reminders —
 * whichever it landed in — and the tasks board, because a reminder is a task.
 */
export function useAcceptChatProposal(threadId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (proposalId: string): Promise<ChatProposalDecision> =>
      data.acceptChatProposal(proposalId),
    retry: false,
    onSuccess: (decision) => {
      if (threadId) void qc.invalidateQueries({ queryKey: ['chatThread', threadId] })
      if (decision.created.kind === 'calendar_block') {
        void qc.invalidateQueries({ queryKey: ['calendar'] })
      } else {
        void qc.invalidateQueries({ queryKey: ['tasks'] })
        void qc.invalidateQueries({ queryKey: remindersKey })
      }
      // What was created is not echoed in enough detail to know whether it carries a time, so
      // the phone is asked either way — one extra check, never a missed reminder.
      armPhoneAlarmsNow()
    },
  })
}

/** Say no. Nothing was created, so nothing outside the thread needs re-reading. */
export function useRejectChatProposal(threadId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (proposalId: string) => data.rejectChatProposal(proposalId),
    retry: false,
    onSuccess: () => {
      if (threadId) void qc.invalidateQueries({ queryKey: ['chatThread', threadId] })
    },
  })
}

/**
 * What a pasted link is, before it is saved.
 *
 * `staleTime: Infinity` because a link's title does not change while a palette is open, and the
 * palette re-renders on every keystroke — without it, typing one more character would re-ask.
 */
export function useClipPreview(url: string | undefined) {
  return useQuery({
    queryKey: ['clipPreview', url],
    queryFn: () => data.getClipPreview(url!),
    enabled: Boolean(url),
    retry: false,
    staleTime: Infinity,
  })
}

/**
 * The one capture write.
 *
 * It invalidates BOTH domains it can land in, not the one it happened to land in this time: a
 * palette that filed a link into Interests and refreshed only Tasks would leave the screen behind
 * it quietly one item short, and the whole promise of ⌘K is that the thing you captured is
 * actually there afterwards.
 */
export function useCaptureQuick() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CaptureInput) => data.captureQuick(input),
    onSuccess: (_result, input) => {
      // A Reminder always carries a time (the adapter's 9:00 default when no chip was tapped).
      if (input.type === 'reminder' || (input.reminderAt ?? null) !== null) armPhoneAlarmsNow()
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: ['reminders'] })
      void qc.invalidateQueries({ queryKey: ['interestSaves'] })
      void qc.invalidateQueries({ queryKey: ['interestTopics'] })
      void qc.invalidateQueries({ queryKey: ['interestsOverview'] })
      // The scope sizes are counts over exactly these corpora, so a capture moves them too.
      void qc.invalidateQueries({ queryKey: ['chatScopes'] })
    },
  })
}

// ── notifications · Web Push (NOTIF-1b) ─────────────────────────────────────
//
// THE SCREEN READS FOUR THINGS AND WRITES FOUR. The four reads are independent on purpose — the
// device list, the settings, the log and the VAPID key answer different questions, and a single
// combined read would make one 503 (no keys configured) blank the other three.
//
// `usePushPublicKey` IS A READ, NOT A STEP OF SUBSCRIBING. lib/push.ts fetches the key itself
// when Enable is pressed; this hook exists so the screen can say "this deployment has no signing
// keys" BEFORE the owner presses a switch that could never have worked. Same endpoint, different
// question — and `retry: false`, because a 503 here is an answer, not a flake.

const pushDevicesKey = ['pushDevices'] as const
const pushPublicKeyKey = ['pushPublicKey'] as const
const notificationSettingsKey = ['notificationSettings'] as const
const notificationLogKey = (filter?: NotificationFilter) => ['notifications', filter] as const

export function usePushDevices() {
  return useQuery({ queryKey: pushDevicesKey, queryFn: () => data.getPushSubscriptions() })
}

export function usePushPublicKey() {
  return useQuery({
    queryKey: pushPublicKeyKey,
    queryFn: () => data.getPushPublicKey(),
    retry: false,
    staleTime: Infinity,
  })
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationSettingsKey,
    queryFn: () => data.getNotificationSettings(),
  })
}

export function useNotificationLog(filter?: NotificationFilter) {
  return useQuery({
    queryKey: notificationLogKey(filter),
    queryFn: () => data.getNotifications(filter),
  })
}

/**
 * WHAT THIS DEVICE ACTUALLY IS — the browser's own subscription compared against the server's
 * list, which is the only way to tell "enabled" from "the browser thinks so".
 *
 * It is a query rather than a piece of component state because it has to be re-read after every
 * write on the screen, and because the browser can lose a subscription without asking anyone.
 * `flushPendingResubscription` runs first: a renewal the service worker staged while nobody was
 * looking is posted before the state it would change is read.
 */
export function usePushState() {
  return useQuery({
    queryKey: ['pushState'],
    queryFn: async () => {
      await flushPendingResubscription(data)
      return subscriptionState(data)
    },
  })
}

function usePushInvalidation() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['pushState'] })
    void qc.invalidateQueries({ queryKey: pushDevicesKey })
  }
}

/** Enable push on THIS browser: the permission prompt, the subscription, and the POST. */
export function useEnablePush() {
  const invalidate = usePushInvalidation()
  return useMutation({
    mutationFn: (label?: string) => subscribe(data, label),
    onSuccess: invalidate,
  })
}

/** Turn this browser off — the server row and the browser's own subscription, in that order. */
export function useDisablePush() {
  const invalidate = usePushInvalidation()
  return useMutation({
    mutationFn: () => unsubscribe(data),
    onSuccess: invalidate,
  })
}

/**
 * Remove ONE device from the list — including, possibly, this one.
 *
 * When the row being removed is this browser's own, the browser's subscription has to go with it
 * or the switch would still read "on" against a row that no longer exists. `unsubscribe` does
 * both halves; a row belonging to another device is a plain delete.
 */
export function useRemovePushDevice() {
  const invalidate = usePushInvalidation()
  return useMutation({
    mutationFn: async (device: { id: string; endpoint: string }) => {
      const mine = await browserEndpoint()
      if (mine && mine === device.endpoint) {
        await unsubscribe(data)
        return
      }
      await data.unsubscribeFromPush(device.id)
    },
    onSuccess: invalidate,
  })
}

/** The test send. It writes a row, so the log is invalidated with everything else. */
export function useSendTestNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => data.sendTestNotification(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
      void qc.invalidateQueries({ queryKey: pushDevicesKey })
      void qc.invalidateQueries({ queryKey: ['pushState'] })
    },
  })
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: NotificationSettingsPatch) => data.updateNotificationSettings(patch),
    // The PUT answers with the WHOLE settings object, merged — so the cache takes that answer
    // rather than being invalidated into a second round trip that would flicker the switches.
    onSuccess: (settings) => qc.setQueryData(notificationSettingsKey, settings),
  })
}

// ── prefetch (cold-cache mitigation) ─────────────────────────────────────────
//
// A session's first navigation is always cold: nothing has been read yet, so the very first
// screen it lands on has to show its own skeleton no matter what. But every screen AFTER that one
// only needs to skeleton-flash if this session has never asked for its data before — and for the
// five screens behind the rail (Today, Tasks, Calendar, Learning, School), the answer to "will
// this be asked for soon" is "almost certainly, within the first minute". So the moment the
// session is known (AuthGate.tsx, once for 'demo' and once for 'signed-in'), this warms exactly
// those caches, using the SAME query keys and query functions the screens' own hooks use above —
// not copies of them — so a warmed entry is guaranteed to be the entry a hook actually reads.
//
// NEVER a stub. Anything that still throws NotImplementedError is absent from this list on
// purpose: warming the cache with a refusal that main.tsx's `shouldRetry` was just taught to
// stop retrying would only mean paying for the throw twice. Two REAL methods also stay out,
// each for its own reason: `getClassOccurrences` (real in both modes) is date-scoped rather
// than a headline read, so the Today screen's own hook warms it; `getJournalEntries`/
// `getJournalStats` (real since V6) belong to the archive, and this list warms the five
// screens behind the rail that a session almost always opens — the archive is not one.
export async function prefetchCoreScreens(queryClient: QueryClient): Promise<void> {
  // Best-effort throughout: a failure here is invisible by design, and the screen that wanted the
  // data falls back to its own normal loading state exactly as if this function did not exist.
  const prefetch = <T>(queryKey: readonly unknown[], queryFn: () => Promise<T>) =>
    queryClient
      .prefetchQuery({ queryKey: queryKey as unknown[], queryFn })
      .catch(() => {
        /* silent — see file-header comment */
      })

  // THE ONE THING HERE THAT IS NOT A READ: a push subscription the service worker renewed while
  // no page was open. `pushsubscriptionchange` fires with no session and no JWT, so push-sw.js
  // stages the new subscription and this — the first thing that runs once a session exists — is
  // "the next app open" it was staged for. Without it the renewal would wait until the owner
  // happened to visit Settings → Notifications, and until then every notification would go to an
  // endpoint that no longer exists.
  //
  // Best-effort like everything else in this function: `flushPendingResubscription` never throws,
  // and a failed POST leaves the renewal staged for the open after this one.
  void flushPendingResubscription(data)

  // The clock decides what "today" means for every date-scoped query below, so it goes first;
  // everything else can fire concurrently once it is known.
  await prefetch(clockKey, () => data.getClock())
  const clock = queryClient.getQueryData<Clock>(clockKey)
  const today = clock?.date

  const jobs: Promise<void>[] = [
    prefetch(termKey, () => data.getTerm()),

    // Reminders and due-soon — the two cards in Today's 2x2 grid that do not need a date.
    prefetch(remindersKey, () => data.getReminders()),
    prefetch(assignmentsKey({ openOnly: true, limit: 3 }), () =>
      data.getAssignments({ openOnly: true, limit: 3 }),
    ),

    // Tasks board — the same no-filter call the Tasks screen makes.
    prefetch(tasksKey(undefined), () => data.getTasks(undefined)),

    // Learning tracks (the Learning overview's headline read).
    prefetch(learningTrackSummariesKey, () => data.getLearningTrackSummaries()),

    // School courses (the School overview's headline read).
    prefetch(coursesKey, () => data.getCourses()),
  ]

  // Everything keyed on today's date, plus the current week: only possible once the clock above
  // actually answered. If it did not (offline, signed out mid-flight), skip these rather than
  // prefetching against a guessed date that would disagree with what the screen asks for.
  if (today) {
    jobs.push(
      prefetch(journalEntryKey(today), () => data.getJournalEntry(today)),
      prefetch(voiceMemosKey(today), () => data.getVoiceMemos(today)),
      prefetch(learningItemsKey(today), () => data.getLearningItems(today)),
    )

    const weekStart = startOfWeek(today)
    const currentWeek: DateRange = { from: weekStart, to: addDays(weekStart, 6) }
    jobs.push(prefetch(calendarKey(currentWeek), () => data.getCalendarRange(currentWeek)))
  }

  await Promise.all(jobs)
}
